import type { UploadReport, UploadSource } from "@/lib/uploadConstants";
import { API_URL } from "@/lib/utils";
import { apiDownloadFile, apiFetchJson } from "@/services/apiClient";
import { trackedFetch } from "@/services/serverStatus";

export type UploadLogStatus =
  | "success"
  | "validation_failed"
  | "duplicate_detected"
  | "upload_failed";

export interface UploadLogEntry {
  id: string;
  filename: string;
  source: UploadSource;
  report: string;
  year: number | null;
  raw_path: string | null;
  parquet_path: string | null;
  uploaded_by: string;
  uploaded_at: string;
  status: UploadLogStatus;
  error_message: string | null;
}

export interface UploadLogsPage {
  items: UploadLogEntry[];
  total: number;
  skip: number;
  limit: number;
}

export interface FetchUploadLogsParams {
  source?: UploadSource;
  report?: UploadReport;
  year?: number;
  status?: UploadLogStatus;
  uploaded_by?: string;
  skip?: number;
  limit?: number;
}


interface UploadFieldError {
  loc?: (string | number)[];
  msg?: string;
}

interface UploadErrorDetail {
  message?: string;
  errors?: UploadFieldError[];
  duplicate_row_count?: number;
}

function formatUploadErrorDetail(detail: UploadErrorDetail): string {
  if (!detail.message) return "File upload failed";
  const parts = [detail.message];

  if (typeof detail.duplicate_row_count === "number") {
    const n = detail.duplicate_row_count;
    parts.push(`(${n} duplicate row${n === 1 ? "" : "s"})`);
  }

  if (detail.errors?.length) {
    const shown = detail.errors.slice(0, 3).map(({ loc, msg }) => {
      const [rowIndex, field] = loc ?? [];
      const where = [
        typeof rowIndex === "number" ? `row ${rowIndex + 1}` : null,
        typeof field === "string" ? field : null,
      ].filter(Boolean).join(", ");
      return where ? `${where} — ${msg}` : msg;
    }).filter((line): line is string => Boolean(line));

    if (shown.length) {
      const remaining = detail.errors.length - shown.length;
      parts.push(shown.join("; ") + (remaining > 0 ? ` (+${remaining} more)` : ""));
    }
  }

  return parts.join(" ");
}

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body?.detail === "string") return body.detail;
    if (body?.detail && typeof body.detail === "object" && !Array.isArray(body.detail)) {
      return formatUploadErrorDetail(body.detail);
    }
    if (Array.isArray(body?.detail) && body.detail[0]?.msg) return body.detail[0].msg;
  } catch {
    /* ignore */
  }
  return fallback;
}

export interface UploadFileArgs {
  source: UploadSource;
  report: UploadReport;
  year?: number;
  file: File;
}

export async function uploadFile({ source, report, year, file }: UploadFileArgs): Promise<void> {
  const body = new FormData();
  body.append("source", source);
  body.append("report", report);
  if (year !== undefined) body.append("year", String(year));
  body.append("file", file);

  const token = localStorage.getItem("access_token");
  const res = await trackedFetch(`${API_URL}/blob/upload`, {
    method: "POST",
    body,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  if (res.status === 401) {
    window.dispatchEvent(new Event("auth:logout"));
    throw new Error("Your session expired. Please sign in again.");
  }
  if (!res.ok) throw new Error(await readError(res, "File upload failed"));
}

export async function fetchUploadLogs(
  params: FetchUploadLogsParams = {},
): Promise<UploadLogsPage> {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.append(key, String(value));
  });
  const qs = query.toString();
  return apiFetchJson<UploadLogsPage>(
    `${API_URL}/blob/uploads${qs ? `?${qs}` : ""}`,
  );
}

export interface LatestUploadEntry {
  id: string;
  report: string;
  filename: string;
  year: number | null;
  uploaded_by: string;
  uploaded_at: string;
}

export type LatestUploadsBySource = Record<UploadSource, LatestUploadEntry[]>;

export async function fetchLatestUploads(): Promise<LatestUploadsBySource> {
  return apiFetchJson<LatestUploadsBySource>(`${API_URL}/blob/uploads/latest`);
}

export async function downloadLatestUpload(
  uploadId: string,
  filename: string,
): Promise<void> {
  return apiDownloadFile(`${API_URL}/blob/download/${uploadId}`, filename);
}
